import React, { useCallback, useEffect, useState } from "react";
import { Button, Drawer, Empty, Input, Select, Space, Table, Tag, Typography } from "antd";
import type { SorterResult, SortOrder } from "antd/es/table/interface";
import { DownloadOutlined } from "@ant-design/icons";
import ExclusionReasons from "./ExclusionReasons";
import HeaderHelp, { AIRING_COLUMN_HELP, ModelHelp, SCORE_COLUMN_HELP, ScopeHelp, ScoreSeHelp, SeriesHelp } from "./HeaderHelp";
import {
  EpisodeAiringRow,
  ScoreFilterOptions,
  ScoreFilters,
  ScoreRow,
  SortColumn,
  downloadScoresCsv,
  fetchEpisodeAirings,
  fetchScoreFilterOptions,
  fetchScores,
} from "./api";

const DAY_NAME = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const pct = (v: number) => (v >= 0 ? "+" : "") + Number(v).toFixed(1) + "%";

const { Text } = Typography;
const BUCKET_COLOR: Record<string, string> = { high: "green", average: "default", low: "volcano" };

const MODEL_LABEL: Record<string, string> = { v1: "V1", m4: "M4" };
const modelLabel = (model: string) => MODEL_LABEL[model] || model.toUpperCase();

/** Table column -> sort key the backend accepts. Only these columns sort; the rest are filters. */
const SORTABLE: Record<string, SortColumn> = { SCORE_PCT: "score_pct", SCORE_SE: "score_se", AIRINGS: "airings" };
const DEFAULT_SORT: { sort: SortColumn; order: "asc" | "desc" } = { sort: "score_pct", order: "desc" };

/** The hand-over table EPISODE_SCORES with filters. Defaults to the usable view: the canonical model, feed
 * scope, strong confidence. Every model the job ran is in the table; the Model filter picks one. */
const ScoresTab: React.FC<{ refreshKey: number }> = ({ refreshKey }) => {
  const [options, setOptions] = useState<ScoreFilterOptions | null>(null);
  const [filters, setFilters] = useState<ScoreFilters>({ scope: "feed", confidence: "strong", ...DEFAULT_SORT });
  const [rows, setRows] = useState<ScoreRow[]>([]);
  const [total, setTotal] = useState(0);
  const [hiddenByConfidence, setHiddenByConfidence] = useState(0);
  const [page, setPage] = useState({ current: 1, pageSize: 50 });
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<ScoreRow | null>(null);
  const [airings, setAirings] = useState<EpisodeAiringRow[]>([]);
  const [airingsLoading, setAiringsLoading] = useState(false);

  const openAirings = (row: ScoreRow) => {
    setSelected(row);
    setAiringsLoading(true);
    fetchEpisodeAirings(row.REF_ID, { feed: row.SCOPE === "overall" ? null : row.FEED_CODE, platform: row.PLATFORM })
      .then((data) => setAirings(data.rows))
      .finally(() => setAiringsLoading(false));
  };

  useEffect(() => {
    fetchScoreFilterOptions().then((o) => {
      setOptions(o);
      setFilters((f) => {
        const next = { ...f };
        if (!next.feed && o.feeds.length) next.feed = o.feeds.includes("NINTL1") ? "NINTL1" : o.feeds[0];
        if (!next.model) next.model = o.defaultModel || o.models?.[0]?.model;
        return next;
      });
    });
  }, [refreshKey]);

  /** Models that have rows for a scope. M4 pools platforms, so it has no platform-scope rows. */
  const modelsForScope = (scope: string) => (options?.models || []).filter((m) => m.scopes.includes(scope)).map((m) => m.model);
  const modelHasScope = (model: string | undefined, scope: string) => !options?.models?.length || modelsForScope(scope).includes(model || "");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchScores(filters, page.current, page.pageSize);
      setRows(data.rows);
      setTotal(data.total);
      setHiddenByConfidence(Math.max(0, (data.totalAllConfidence ?? data.total) - data.total));
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const update = (patch: Partial<ScoreFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage((p) => ({ ...p, current: 1 }));
  };

  /** antd's sort indicator for a column, driven by the filters so it matches what the server returned. */
  const sortOrderFor = (dataIndex: string): SortOrder =>
    filters.sort === SORTABLE[dataIndex] ? (filters.order === "asc" ? "ascend" : "descend") : null;

  /** A header click: antd reports the new column and direction (or none on the third click, which
   * returns to the default order). The rows themselves come back sorted from the server. */
  const onSort = (sorter: SorterResult<ScoreRow> | SorterResult<ScoreRow>[]) => {
    const single = Array.isArray(sorter) ? sorter[0] : sorter;
    const column = SORTABLE[String(single?.field ?? single?.columnKey ?? "")];
    if (!column || !single?.order) return update(DEFAULT_SORT);
    return update({ sort: column, order: single.order === "ascend" ? "asc" : "desc" });
  };

  return (
    <div style={{ padding: 24 }}>
      <Space wrap style={{ marginBottom: 16 }}>
        <ModelHelp />
        <Select
          value={filters.model}
          style={{ width: 90 }}
          onChange={(model) => update({ model })}
          options={(options?.models || []).map((m) => ({
            value: m.model,
            label: modelLabel(m.model),
            disabled: !m.scopes.includes(filters.scope),
          }))}
        />
        <ScopeHelp />
        <Select
          value={filters.scope}
          style={{ width: 130 }}
          onChange={(scope) =>
            update({
              scope,
              model: modelHasScope(filters.model, scope) ? filters.model : modelsForScope(scope)[0] || filters.model,
              feed: scope === "overall" ? undefined : filters.feed,
              platform: undefined,
            })
          }
          options={[
            { value: "feed", label: "Per feed" },
            { value: "platform", label: "Per platform" },
            { value: "overall", label: "Overall" },
          ].map((option) => ({
            ...option,
            // M4 pools platforms per airing, so it has no per-platform rows: grey the scope out for it.
            disabled: !modelHasScope(filters.model, option.value),
            title: modelHasScope(filters.model, option.value) ? undefined : `${modelLabel(filters.model || "")} has no ${option.label.toLowerCase()} scores`,
          }))}
        />
        {filters.scope !== "overall" && (
          <Select
            allowClear
            placeholder="Feed"
            value={filters.feed}
            style={{ width: 150 }}
            onChange={(feed) => update({ feed })}
            options={(options?.feeds || []).map((f) => ({ value: f, label: f }))}
          />
        )}
        {filters.scope === "platform" && (
          <Select
            allowClear
            placeholder="Platform"
            value={filters.platform}
            style={{ width: 230 }}
            onChange={(platform) => update({ platform })}
            options={(options?.platforms || []).map((p) => ({ value: p, label: p }))}
          />
        )}
        <SeriesHelp />
        <Select
          allowClear
          placeholder="Series"
          value={filters.series}
          style={{ width: 200 }}
          onChange={(series) => update({ series })}
          options={(options?.series || []).map((s) => ({ value: s, label: s }))}
        />
        <Select
          allowClear
          placeholder="Confidence"
          value={filters.confidence}
          style={{ width: 140 }}
          onChange={(confidence) => update({ confidence })}
          options={[
            { value: "strong", label: "Strong only" },
            { value: "thin", label: "Thin only" },
          ]}
        />
        <Select
          allowClear
          placeholder="Bucket"
          value={filters.bucket}
          style={{ width: 130 }}
          onChange={(bucket) => update({ bucket })}
          options={[
            { value: "high", label: "High only" },
            { value: "average", label: "Average only" },
            { value: "low", label: "Low only" },
          ]}
        />
        <Input.Search placeholder="Title or ref id" allowClear style={{ width: 240 }} onSearch={(q) => update({ q: q || undefined })} />
        <Button icon={<DownloadOutlined />} onClick={() => downloadScoresCsv(filters)}>
          Download CSV
        </Button>
        {options?.asOfDate && (
          <Text type="secondary">
            Data through {options.asOfDate}
            {options.computedAt ? `, computed ${new Date(options.computedAt).toLocaleString()}` : ""}
          </Text>
        )}
      </Space>

      <Table
        size="middle"
        rowKey={(r) => `${r.MODEL}|${r.REF_ID}|${r.SCOPE}|${r.FEED_CODE}|${r.PLATFORM}`}
        loading={loading}
        dataSource={rows}
        onRow={(row) => ({ onClick: () => openAirings(row), style: { cursor: "pointer" } })}
        onChange={(_pagination, _tableFilters, sorter, extra) => {
          if (extra.action === "sort") onSort(sorter);
        }}
        showSorterTooltip={{ title: "Sorts the whole table on the server, not just this page" }}
        pagination={{
          current: page.current,
          pageSize: page.pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100, 200],
          onChange: (current, pageSize) => setPage({ current, pageSize }),
          onShowSizeChange: (_current, pageSize) => setPage({ current: 1, pageSize }),
        }}
        scroll={{ x: filters.scope === "platform" ? 1300 : 1100 }}
        locale={{
          emptyText:
            !loading && total === 0 && hiddenByConfidence > 0 ? (
              <Empty
                description={
                  <>
                    No {filters.confidence} scores match, but {hiddenByConfidence.toLocaleString()} {filters.confidence === "strong" ? "thin" : "strong"}{" "}
                    {hiddenByConfidence === 1 ? "one does" : "ones do"}.{" "}
                    <Button type="link" style={{ padding: 0 }} onClick={() => update({ confidence: undefined })}>
                      Show all confidence levels
                    </Button>
                  </>
                }
              />
            ) : (
              <Empty description={filters.series || filters.q ? "No scores match these filters" : "No scores yet. Upload a report on the Upload tab."} />
            ),
        }}
        columns={[
          { title: "Series", dataIndex: "SERIES", width: 170, ellipsis: true },
          { title: "Title", dataIndex: "TITLE", ellipsis: true },
          { title: "Ref ID", dataIndex: "REF_ID", width: 110 },
          ...(filters.scope === "platform" ? [{ title: "Platform", dataIndex: "PLATFORM", width: 200, ellipsis: true }] : []),
          { title: <HeaderHelp label="Score %" help={SCORE_COLUMN_HELP.score} />, dataIndex: "SCORE_PCT", width: 130, align: "right" as const, sorter: true, sortOrder: sortOrderFor("SCORE_PCT"), render: (v: number) => (v >= 0 ? "+" : "") + Number(v).toFixed(1) },
          { title: <HeaderHelp label="± SE" help={SCORE_COLUMN_HELP.se} />, dataIndex: "SCORE_SE", width: 100, align: "right" as const, sorter: true, sortOrder: sortOrderFor("SCORE_SE"), render: (v: number | null) => (v === null || v === undefined ? "" : "±" + Number(v).toFixed(1)) },
          { title: <HeaderHelp label="Bucket" help={SCORE_COLUMN_HELP.bucket} />, dataIndex: "BUCKET", width: 120, align: "center" as const, render: (v: string) => <Tag color={BUCKET_COLOR[v] || "default"}>{v}</Tag> },
          { title: "Airings", dataIndex: "AIRINGS", width: 120, align: "center" as const, sorter: true, sortOrder: sortOrderFor("AIRINGS") },
          { title: <HeaderHelp label="Confidence" help={SCORE_COLUMN_HELP.confidence} />, dataIndex: "CONFIDENCE", width: 140, align: "center" as const },
          { title: <HeaderHelp label="Source" help={SCORE_COLUMN_HELP.source} />, dataIndex: "SOURCE", width: 120, align: "center" as const, render: (v: string) => (v === "borrowed" ? <Tag>borrowed</Tag> : v) },
        ]}
      />

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        width={1280}
        title={selected ? `${selected.SERIES}: ${selected.TITLE} (${selected.REF_ID})` : ""}
      >
        {selected && (
          <>
            <Space style={{ marginBottom: 12 }} wrap>
              <Tag>{modelLabel(selected.MODEL)}</Tag>
              <Text>
                Score <Text strong>{pct(selected.SCORE_PCT)}</Text>
                {selected.SCORE_SE !== null && selected.SCORE_SE !== undefined ? (
                  <>
                    <Text type="secondary"> ±{Number(selected.SCORE_SE).toFixed(1)}</Text> <ScoreSeHelp />
                  </>
                ) : null}
              </Text>
              {selected.MODEL !== "m4" && airings.length > 0 && (
                <Text type="secondary">
                  (plain average of the {airings.length} airings below:{" "}
                  {pct(airings.reduce((sum, a) => sum + a.airingPct, 0) / airings.length)})
                </Text>
              )}
              <Tag color={BUCKET_COLOR[selected.BUCKET] || "default"}>{selected.BUCKET}</Tag>
              <Text type="secondary">
                {selected.SCOPE === "platform" ? selected.PLATFORM : selected.SCOPE === "feed" ? `feed ${selected.FEED_CODE}` : "overall"},{" "}
                {selected.AIRINGS} airings, {selected.CONFIDENCE}
              </Text>
            </Space>
            <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
              {selected.MODEL === "m4" ? (
                <>
                  The airings behind this score, shown against V1&apos;s slot baselines for orientation. M4 itself
                  removes the day, slot, series, lead-in, repeat-fatigue and ad-load effects from each airing, pools the
                  platforms that carried the same airing into one observation, and shrinks the episode&apos;s average
                  toward its series by how much evidence it has, so its score will not equal the plain average of the
                  &quot;Airing vs slot&quot; column. The ± figure is the score&apos;s margin of error: the true effect most
                  likely lies within one SE of the score. Airings the cleaning step excluded are not listed.{" "}
                  <ExclusionReasons />
                </>
              ) : (
                <>
                  Each airing against the slot it landed in: what an average episode of this series earns there
                  (expected), what this airing earned (rate), and the difference. The score starts as the plain
                  average of the &quot;Airing vs slot&quot; column, is pulled toward 0 when there are few airings, and for
                  a feed is blended across its platforms by minutes watched. Airings the cleaning step excluded are not
                  listed. <ExclusionReasons />
                </>
              )}
            </Text>
            <Table
              size="small"
              rowKey={(r) => `${r.platform}|${r.channel}|${r.startLocal}`}
              loading={airingsLoading}
              dataSource={airings}
              pagination={{ defaultPageSize: 25, showSizeChanger: true, pageSizeOptions: [25, 50, 100, 200] }}
              scroll={{ x: 1220 }}
              columns={[
                { title: "Aired (local)", dataIndex: "startLocal", width: 150, render: (v: string) => v?.replace("T", " ").slice(0, 16) },
                { title: "Slot", key: "slot", width: 90, render: (_: unknown, r: EpisodeAiringRow) => `${DAY_NAME[r.dayOfWeek]} ${String(r.hourOfDay).padStart(2, "0")}:00` },
                { title: "Platform", dataIndex: "platform", width: 190, ellipsis: true },
                { title: "Channel", dataIndex: "channel", width: 140, ellipsis: true },
                { title: <HeaderHelp label="Slot vs average" help={AIRING_COLUMN_HELP.slotVsAverage} />, dataIndex: "baselineIndex", width: 140, align: "right", render: (v: number) => pct((v - 1) * 100) },
                { title: <HeaderHelp label="Expected rate" help={AIRING_COLUMN_HELP.expectedRate} />, dataIndex: "expectedRate", width: 130, align: "right", render: (v: number) => v.toFixed(2) },
                { title: <HeaderHelp label="Actual rate" help={AIRING_COLUMN_HELP.actualRate} />, dataIndex: "rate", width: 120, align: "right", render: (v: number) => v.toFixed(2) },
                { title: <HeaderHelp label="Airing vs slot" help={AIRING_COLUMN_HELP.airingVsSlot} />, dataIndex: "airingPct", width: 130, align: "right", render: (v: number) => <Text type={v >= 0 ? "success" : "danger"}>{pct(v)}</Text>, sorter: (a, b) => a.airingPct - b.airingPct },
                { title: "Minutes watched", dataIndex: "viewershipMin", width: 100, align: "right", render: (v: number) => Math.round(v).toLocaleString() },
                { title: <HeaderHelp label="Merged pieces" help={AIRING_COLUMN_HELP.mergedPieces} />, dataIndex: "mergedRows", width: 130, align: "right", render: (v: number) => (v > 1 ? v : "") },
              ]}
            />
          </>
        )}
      </Drawer>
    </div>
  );
};

export default ScoresTab;
